/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  СПРАВОЧНИК ЗОН ДОСТУПА текущего мероприятия
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ⚠️ **ЭТО НЕ ПРАВА ДОСТУПА.** «Зона разминки», «Микст-зона», «Техническая
 * зона» — это участки ПЛОЩАДКИ, куда человека пускают на самом соревновании.
 * Ни одна проверка прав в этом приложении не имеет права смотреть сюда: за
 * права отвечают `auth.isAdmin` (система) и `event.canManage` (мероприятие).
 *
 * Соблазн здесь даже сильнее, чем у `participantRoles`: слово «доступ» стоит
 * прямо в названии. Именно поэтому тип называется `AccessZone`, а не `Zone` или
 * `Permission`, и живёт в отдельном сторе.
 *
 * Справочник локален для мероприятия (у каждого соревнования своё здание) и
 * пагинации не имеет: это десятки записей, из которых собирают набор отметок на
 * бейдже.
 *
 * ⚠️ Порядок в `items` — **по триграмме**, как его отдал сервер, а не по
 * названию. Пересортировывать на клиенте нельзя: сверяются с бейджем, на
 * котором написано `LASK`, а не «Зона разминки».
 */

import { defineStore } from 'pinia'
import { computed, ref, shallowRef } from 'vue'

import { accessZonesApi } from '@/api/access-zones'
import type { ApiError } from '@/lib/api-error'
import { normalizeError } from '@/lib/api-error'
import type { AccessZone, CreateAccessZoneRequest } from '@/types/api'

export const useAccessZonesStore = defineStore('accessZones', () => {
  /* ───────────────────────── state ───────────────────────── */

  const eventId = ref<string | null>(null)

  /** Отсортирован сервером по триграмме. */
  const items = ref<AccessZone[]>([])

  const loading = ref(false)
  const error = shallowRef<ApiError | null>(null)
  const ready = ref(false)

  /* ───────────────────────── getters ─────────────────────── */

  const isEmpty = computed(() => items.value.length === 0)

  const byId = computed(() => new Map(items.value.map((zone) => [zone.id, zone])))

  /**
   * Зона по id, или `null`.
   *
   * `null` для пустого id и для id, которого в справочнике нет: зону могли
   * удалить, пока экран был открыт. Падать здесь нечему.
   */
  function get(zoneId: string): AccessZone | null {
    if (!zoneId) return null
    return byId.value.get(zoneId) ?? null
  }

  /* ───────────────────────── запрос ──────────────────────── */

  let controller: AbortController | null = null

  async function load(id: string, force = false): Promise<void> {
    if (!force && eventId.value === id && ready.value && error.value === null) return

    controller?.abort()
    const ctl = new AbortController()
    controller = ctl

    if (eventId.value !== id) {
      items.value = []
      ready.value = false
    }

    eventId.value = id
    loading.value = true
    error.value = null

    try {
      const data = await accessZonesApi.list(id, ctl.signal)
      if (controller !== ctl) return
      items.value = data.accessZones
    } catch (err) {
      if (controller !== ctl) return

      const apiError = normalizeError(err)
      if (apiError.isCanceled) return

      error.value = apiError
      items.value = []
    } finally {
      if (controller === ctl) {
        controller = null
        loading.value = false
        ready.value = true
      }
    }
  }

  /* ───────────────────────── мутации ─────────────────────── */

  /** Бросают `ApiError`; вызывающий заворачивает в `useAsyncAction`. */

  async function create(input: CreateAccessZoneRequest): Promise<AccessZone> {
    const id = requireEventId()
    const response = await accessZonesApi.create(id, input)
    await load(id, true)
    return response.accessZone
  }

  async function update(zoneId: string, input: CreateAccessZoneRequest): Promise<AccessZone> {
    const id = requireEventId()
    const response = await accessZonesApi.update(id, zoneId, input)
    await load(id, true)
    return response.accessZone
  }

  async function remove(zoneId: string): Promise<AccessZone> {
    const id = requireEventId()
    const response = await accessZonesApi.remove(id, zoneId)
    await load(id, true)
    return response.accessZone
  }

  function requireEventId(): string {
    const id = eventId.value
    if (!id) throw new Error('[accessZones] мутация вне скоупа мероприятия')
    return id
  }

  /* ───────────────────────── $reset ──────────────────────── */

  function $reset(): void {
    controller?.abort()
    controller = null

    eventId.value = null
    items.value = []
    loading.value = false
    error.value = null
    ready.value = false
  }

  return {
    // state
    eventId,
    items,
    loading,
    error,
    ready,
    // getters
    isEmpty,
    byId,
    get,
    // actions
    load,
    create,
    update,
    remove,
    $reset,
  }
})
